import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { getPost } from "../blogs";
import type { ImageContent } from "../blogs";
import "../assets/styles/PageStyles.scss";
import StoreBadges from "../components/StoreBadges";
import Breadcrumbs from "../components/Breadcrumbs";
import NotFound from "./NotFound";

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPost(slug) : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Render center-aligned image
  const renderCenterImage = (item: ImageContent, index: number) => (
    <div key={index} className="my-8 flex justify-center">
      <div style={{ display: "inline-block" }}>
        <div
          style={{
            display: "inline-block",
            borderRadius: "15px",
            border: "2px solid #ccc",
            padding: "5px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <img
            src={item.src}
            alt={item.alt}
            width={item.width}
            height={item.height}
            loading="lazy"
            style={{
              display: "block",
              maxWidth: "min(800px, 100%)",
              maxHeight: "600px",
              width: "auto",
              height: "auto",
              borderRadius: "10px",
            }}
          />
        </div>
        {item.caption && (
          <p
            className="text-center italic opacity-75"
            style={{
              fontSize: "0.75rem",
              lineHeight: "1.2rem",
              padding: "4px 0",
              margin: "8px 0 0 0",
            }}
          >
            {item.caption}
          </p>
        )}
      </div>
    </div>
  );

  // Render left/right floating image
  const renderFloatingImage = (item: ImageContent, index: number) => {
    const isLeft = item.position === "left";
    const floatStyle = isLeft ? "left" : "right";
    const marginStyle = isLeft
      ? { marginRight: "24px", marginBottom: "16px" }
      : { marginLeft: "24px", marginBottom: "16px" };

    return (
      <div
        key={index}
        style={{
          float: floatStyle,
          maxWidth: "min(300px, 40%)",
          ...marginStyle,
        }}
      >
        <div
          style={{
            display: "inline-block",
            borderRadius: "15px",
            border: "2px solid #ccc",
            padding: "5px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <img
            src={item.src}
            alt={item.alt}
            width={item.width}
            height={item.height}
            loading="lazy"
            style={{
              display: "block",
              maxWidth: "100%",
              maxHeight: "500px",
              width: "auto",
              height: "auto",
              borderRadius: "10px",
            }}
          />
        </div>
        {item.caption && (
          <p
            className="text-center italic opacity-75"
            style={{
              fontSize: "0.75rem",
              lineHeight: "1.2rem",
              padding: "4px 0",
              margin: "8px 0 0 0",
            }}
          >
            {item.caption}
          </p>
        )}
      </div>
    );
  };

  // Turn [label](/url) links and **bold** spans inside a content string into
  // real elements, so blog posts can cross-link to each other/feature pages
  // and emphasize inline text anywhere, not just at the start of a
  // paragraph. Internal (/-prefixed) link targets use react-router's Link
  // so client-side routing and trailing slashes stay correct; anything else
  // renders as a normal external anchor.
  const renderInlineMarkdown = (text: string, keyPrefix: string): React.ReactNode[] => {
    const inlinePattern = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let matchIndex = 0;

    while ((match = inlinePattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(text.slice(lastIndex, match.index));
      }
      const [, linkLabel, href, boldText] = match;
      const key = `${keyPrefix}-md-${matchIndex++}`;
      if (boldText !== undefined) {
        // Recurse so a link nested inside bold (e.g. "**[xG](/url/)**")
        // still renders as a link instead of literal bracket/paren text.
        nodes.push(<strong key={key}>{renderInlineMarkdown(boldText, key)}</strong>);
      } else if (href.startsWith("/")) {
        nodes.push(
          <Link key={key} to={href}>
            {linkLabel}
          </Link>
        );
      } else {
        nodes.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {linkLabel}
          </a>
        );
      }
      lastIndex = inlinePattern.lastIndex;
    }

    if (lastIndex < text.length) {
      nodes.push(text.slice(lastIndex));
    }

    return nodes;
  };

  // Render text paragraph with formatting
  const renderParagraph = (paragraph: string, index: number) => {
    // Handle headers
    if (paragraph.startsWith("## ")) {
      return (
        <h2 key={index} className="mt-6 mb-4">
          {paragraph.replace("## ", "")}
        </h2>
      );
    }

    // Handle level 3 headers
    if (paragraph.startsWith("### ")) {
      return (
        <h3 key={index} className="mt-5 mb-3 text-xl font-bold">
          {paragraph.replace("### ", "")}
        </h3>
      );
    }

    // Handle bullet points ("• " single bullet, or "- " multi-line list —
    // both conventions show up across posts)
    if (paragraph.startsWith("• ")) {
      return (
        <p key={index} className="ml-4 mb-2">
          <span className="text-primary">●</span>{" "}
          {renderInlineMarkdown(paragraph.replace("• ", ""), `b-${index}`)}
        </p>
      );
    }

    if (paragraph.startsWith("- ") || paragraph.includes("\n- ")) {
      const items = paragraph
        .split("\n")
        .map((line) => line.replace(/^-\s*/, "").trim())
        .filter(Boolean);
      return (
        <ul key={index} className="mb-4 ml-4">
          {items.map((item, itemIndex) => (
            <li key={itemIndex} className="mb-1">
              {renderInlineMarkdown(item, `li-${index}-${itemIndex}`)}
            </li>
          ))}
        </ul>
      );
    }

    // Handle bold with colon
    if (paragraph.startsWith("**") && paragraph.includes("**:")) {
      const [title, ...rest] = paragraph.split("**:");
      return (
        <p key={index} className="mt-4 mb-2">
          <strong>{title.replace("**", "")}:</strong>
          {renderInlineMarkdown(rest.join("**:"), `bc-${index}`)}
        </p>
      );
    }

    // Regular paragraph
    return (
      <p key={index} className="mb-4">
        {renderInlineMarkdown(paragraph, `p-${index}`)}
      </p>
    );
  };

  if (!post) {
    // Hosting answers an unknown slug with 404.html (status 404), the
    // prerendered NotFound page: render the same page so hydration matches.
    return <NotFound />;
  }

  const postUrl = `https://goalguru.live/blog/${post.slug}/`;
  // Generated at build time by scripts/generate-og-images.mjs — a branded
  // 1200x630 card with this post's own title, not the one generic image
  // every page used to share. Reused for both the social preview tags and
  // the structured-data image so there's a single source of truth per post.
  const ogImage = `https://goalguru.live/og/${post.slug}.png`;
  // Search results show about 60 characters of a title and 155 of a
  // description: keep the brand suffix only while it fits, and cut long
  // excerpts at a word boundary for the meta description (the page itself
  // still shows the full excerpt).
  const pageTitle =
    post.title.length + " | Goal Guru Blog".length <= 60
      ? `${post.title} | Goal Guru Blog`
      : post.title.length + " | Goal Guru".length <= 62
        ? `${post.title} | Goal Guru`
        : post.title;
  const metaDescription = (() => {
    const text = post.excerpt.replace(/\s+/g, " ").trim();
    if (text.length <= 155) return text;
    const cut = text.slice(0, 154);
    const sentenceEnd = cut.lastIndexOf(". ");
    if (sentenceEnd >= 90) return cut.slice(0, sentenceEnd + 1);
    return `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:—-]+$/, "")}…`;
  })();
  // post.date is a display string ("Aug 11, 2026"). Parsing it plain would
  // read as local midnight and shift by a day once converted to UTC on any
  // build machine east of UTC (confirmed: CEST shifts every date back a
  // day) — appending "UTC" forces a timezone-independent parse.
  const isoDate = new Date(`${post.date} UTC`).toISOString().slice(0, 10);

  return (
    <div className="page-container">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="article" />
        <meta
          property="og:url"
          content={postUrl}
        />
        <meta
          property="og:image"
          content={ogImage}
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta
          name="twitter:image"
          content={ogImage}
        />
        <link
          rel="canonical"
          href={postUrl}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            image: ogImage,
            datePublished: isoDate,
            dateModified: isoDate,
            url: postUrl,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": postUrl,
            },
            author: {
              "@type": "Organization",
              name: "Goal Guru",
              url: "https://goalguru.live",
            },
            publisher: {
              "@type": "Organization",
              name: "Goal Guru",
              logo: {
                "@type": "ImageObject",
                url: "https://goalguru.live/logo192.png",
              },
            },
          })}
        </script>
      </Helmet>

      <Breadcrumbs
        items={[
          { name: "Blog", path: "/blog/" },
          { name: post.title },
        ]}
      />

      <header>
        <h1>{post.title}</h1>
        <p className="date">
          {post.date} · {post.readTime} · {post.category}
        </p>
      </header>

      <article style={{ overflow: "auto" }}>
        {post.content.map((item, index) => {
          // Handle image objects
          if (typeof item === "object" && item.type === "image") {
            const position = item.position || "center";

            return position === "center"
              ? renderCenterImage(item, index)
              : renderFloatingImage(item, index);
          }

          // Handle string content
          return renderParagraph(item as string, index);
        })}
      </article>

      {/* Clear any floating elements */}
      <div style={{ clear: "both" }} />

      <footer className="mt-12 p-6 rounded-lg text-center">
        <h3 className="mb-2">Try These Tactics with Goal Guru</h3>
        <p className="mb-4">
          Download the app and start creating your own Smart Alerts today.
        </p>
        <p className="mb-4">Available on both the App Store and Google Play.</p>
        <StoreBadges height={50} />
      </footer>
    </div>
  );
};

export default BlogPost;
